/**
 * SupplierUnitsCodes
 *
 * Manages the association between a client's codes/units and suppliers. Used inside
 * modals on Admin > Clients: "Code/Supplier Association" (type="codes") and
 * "Unit/Office Association" (type="units"). Loads reference data (available
 * codes or units for the client), then the current associations (which supplier
 * is linked to which code/unit), and lets users add or delete associations.
 *
 * API CONTRACT (see backend ERROR_CONTRACT.md)
 * - Success: list endpoints return { data: [...] }; POST returns { message: "ok", id: "..." }; DELETE returns { message: "ok" }.
 * - Error: all 4xx/5xx responses return { error: "<error_code>" } (e.g. failed_to_load, failed_to_add, failed_to_delete,
 *   or specific codes like client_not_found, supplier_not_found). The component reads err.response.data.error and maps
 *   it to i18n key supplier_units_codes_error_<error_code>, with an operation-based fallback (Option B).
 *
 * ERROR DISPLAY (Option B)
 * - All error messages are shown in a single place: one alert box with class "alert alert-danger".
 * - We store { apiKey, operationKey } when a fetch fails. apiKey comes from the API (err.response.data.error);
 *   operationKey is one of failed_to_load | failed_to_add | failed_to_delete depending on which request failed.
 * - The displayed message is: translation for supplier_units_codes_error_<apiKey> if it exists, otherwise
 *   supplier_units_codes_error_<operationKey>. This gives specific messages when the API sends a code we translate,
 *   and a sensible fallback when the code is missing or unknown.
 *
 * Props:
 *   - type: "codes" | "units"
 *   - clientId: client id for API calls
 *   - data: from MobX inject (data.t for i18n)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer, inject } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import FormTextField from './form-textfield';
import { fetch } from '../utils/api-wrapper';

function SupplierUnitsCodes({ type, clientId, data }) {
  // --- State ---
  /** Current associations for this client. Each item: { _id, code|unit, supplier_name }. */
  const [list, setList] = useState([]);
  /** Reference list of codes/units available for this client. null = not loaded yet; [] = loaded but empty (no codes/units). */
  const [codeUnitList, setCodeUnitList] = useState(null);
  /** Selected supplier's id when the user is adding a new association (from FormTextField search). */
  const [supplierId, setSupplierId] = useState(null);
  /** Display name of the selected supplier (shown in the add row). */
  const [supplierName, setSupplierName] = useState('');
  /** Index into codeUnitList for the code/unit currently selected in the dropdown (add row). */
  const [codeUnitIdx, setCodeUnitIdx] = useState(0);
  /** True once the associations list (refreshList) has finished loading. While false, we show a spinner row in the table. */
  const [listLoaded, setListLoaded] = useState(false);
  /**
   * Error state when any fetch fails (Option B). null when there is no error.
   * When set: { apiKey: string | null, operationKey: 'failed_to_load' | 'failed_to_add' | 'failed_to_delete' }.
   * - apiKey: from API response (err.response.data.error), e.g. "client_not_found" or "failed_to_load".
   * - operationKey: which operation failed, used as fallback if apiKey has no translation.
   */
  const [loadError, setLoadError] = useState(null);
  /** Success feedback: 'add' | 'delete' after a successful action. Shown in green alert; auto-cleared after a few seconds. */
  const [successMessage, setSuccessMessage] = useState(null);
  /** Processing state: 'add' | 'delete' while POST or DELETE is in flight. Shows spinner + message and disables buttons. */
  const [actionInProgress, setActionInProgress] = useState(null);
  /** Ref to avoid setState after unmount. Fetch callbacks check mountedRef.current before calling setState. */
  const mountedRef = useRef(true);
  const successTimeoutRef = useRef(null);
  /** When true, refreshList was just called after a successful add/delete; its error callback should not overwrite the success message. */
  const justSucceededRef = useRef(false);

  /**
   * Resolves the user-visible error message for Option B.
   * 1. If apiKey is present, try i18n key supplier_units_codes_error_<apiKey>.
   * 2. If that translation exists (and is not the key itself), use it.
   * 3. Otherwise use supplier_units_codes_error_<operationKey> (always translated for load/add/delete).
   */
  const getErrorMessage = useCallback(
    (errorState) => {
      const prefix = 'supplier_units_codes_error_';
      const apiKey = errorState.apiKey && typeof errorState.apiKey === 'string' ? errorState.apiKey.trim() : null;
      const candidateKey = apiKey ? `${prefix}${apiKey}` : null;
      const fallbackKey = `${prefix}${errorState.operationKey}`;
      const candidateMsg = candidateKey ? data.t.read(candidateKey) : null;
      if (candidateMsg && candidateMsg !== candidateKey) return candidateMsg;
      return data.t.read(fallbackKey);
    },
    [data]
  );

  /**
   * Fetches the current supplier associations for this client and updates list.
   * Called on initial load (after codeUnitList is set) and after add/delete.
   *
   * @param {boolean} suppressLoading - If true, do not set listLoaded to false (used after add/delete so we don't show the full-page spinner again).
   */
  const refreshList = useCallback((suppressLoading = false) => {
    if (!suppressLoading) {
      setListLoaded(false);
    }

    fetch(
      `/supplier-${type}/${clientId}/assoc`,
      'GET',
      {},
      (response) => {
        if (!mountedRef.current) return;
        setLoadError(null);
        // API contract: list endpoints return { data: [...] }. Axios puts the body in response.data.
        const data = response.data?.data ?? [];
        setList(Array.isArray(data) ? data : []);
        setListLoaded(true);
      },
      (err) => {
        if (!mountedRef.current) return;
        setListLoaded(true);
        if (justSucceededRef.current) {
          justSucceededRef.current = false;
          return;
        }
        const apiKey = err?.response?.data?.error;
        setLoadError({ apiKey: apiKey || null, operationKey: 'failed_to_load' });
      }
    );
  }, [type, clientId]);

  /**
   * On mount (and when type or clientId change): fetch reference codes/units from admin-client-{type},
   * then if any exist, fetch associations via refreshList. Cleanup sets mountedRef to false so in-flight
   * callbacks do not call setState after unmount (e.g. user closes the modal before the request completes).
   */
  useEffect(() => {
    mountedRef.current = true;
    setListLoaded(false);

    fetch(
      `/admin-client-${type}/${clientId}`,
      'GET',
      {},
      (response) => {
        if (!mountedRef.current) return;
        setLoadError(null);
        // API contract: admin-client-{type} returns { data: [...] }.
        const data = response.data?.data ?? [];
        const units = Array.isArray(data) ? data : [];
        setCodeUnitList(units);

        if (units.length > 0) {
          refreshList(false);
        } else {
          setList([]);
          setListLoaded(true);
        }
      },
      (err) => {
        if (!mountedRef.current) return;
        setCodeUnitList([]);
        setListLoaded(true);
        const apiKey = err?.response?.data?.error;
        setLoadError({ apiKey: apiKey || null, operationKey: 'failed_to_load' });
      }
    );

    return () => {
      mountedRef.current = false;
    };
  }, [type, clientId, refreshList]);

  /** Auto-clear success message after 4 seconds; clear timeout on unmount. */
  useEffect(() => {
    if (successMessage == null) return;
    successTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setSuccessMessage(null);
    }, 4000);
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, [successMessage]);

  /**
   * Removes an association by id. Calls DELETE supplier-{type}/{id}. On success, refreshList(true)
   * and show success message. On failure, set loadError. Buttons are disabled while actionInProgress.
   */
  const deleteElement = (id) => {
    setLoadError(null);
    setSuccessMessage(null);
    setActionInProgress('delete');
    fetch(
      `/supplier-${type}/${id}`,
      'DELETE',
      {},
      () => {
        if (!mountedRef.current) return;
        setLoadError(null);
        setActionInProgress(null);
        setSuccessMessage('delete');
        justSucceededRef.current = true;
        refreshList(true);
      },
      (err) => {
        if (!mountedRef.current) return;
        setActionInProgress(null);
        const apiKey = err?.response?.data?.error;
        setLoadError({ apiKey: apiKey || null, operationKey: 'failed_to_delete' });
      }
    );
  };

  /**
   * Creates a new association: selected code/unit (from dropdown) + selected supplier (from FormTextField).
   * POST to admin-supplier-{type}. On success, show success message, clear selection, refreshList(true).
   * On failure, set loadError. Add button is disabled while actionInProgress.
   */
  const addCode = () => {
    if (supplierId != null) {
      setLoadError(null);
      setSuccessMessage(null);
      setActionInProgress('add');
      const dataForm = new FormData();
      const selected = codeUnitList[codeUnitIdx];

      dataForm.append('id_client', clientId);
      dataForm.append('id_supplier', supplierId);
      dataForm.append(
        type === 'codes' ? 'code' : 'unit',
        type === 'codes' ? selected.code : selected.unit
      );

      fetch(
        `/admin-supplier-${type}`,
        'POST',
        dataForm,
        () => {
          if (!mountedRef.current) return;
          setLoadError(null);
          setActionInProgress(null);
          setSuccessMessage('add');
          setSupplierId(null);
          setSupplierName('');
          justSucceededRef.current = true;
          refreshList(true);
        },
        (err) => {
          if (!mountedRef.current) return;
          setActionInProgress(null);
          const apiKey = err?.response?.data?.error;
          setLoadError({ apiKey: apiKey || null, operationKey: 'failed_to_add' });
        }
      );
    }
  };

  // --- Render: message strip (error | processing | success, centered), then loading / empty / table ---

  const renderMessageStrip = () => {
    if (loadError) {
      return (
        <div className="alert alert-danger text-center d-flex justify-content-center align-items-center" role="alert">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
          <span>{getErrorMessage(loadError)}</span>
        </div>
      );
    }
    if (actionInProgress) {
      return (
        <div className="alert alert-info text-center d-flex justify-content-center align-items-center" role="status">
          <FontAwesomeIcon icon={faSpinner} className="mr-2 infinite-rotate" />
          <span>
            {actionInProgress === 'add'
              ? data.t.read('supplier_units_codes_adding')
              : data.t.read('supplier_units_codes_deleting')}
          </span>
        </div>
      );
    }
    if (successMessage) {
      return (
        <div className="alert alert-success text-center d-flex justify-content-center align-items-center" role="status">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          <span>
            {successMessage === 'add'
                ? (data.t.read('supplier_units_codes_success_add') || 'Association added successfully.')
                : (data.t.read('supplier_units_codes_success_delete') || 'Association deleted successfully.')}
          </span>
        </div>
      );
    }
    return null;
  };

  const content = loadError ? null : codeUnitList === null ? (
    <div className="d-flex flex-column align-items-center text-dark py-4">
      <div className="mb-3 font-weight-bold" style={{ fontSize: '1.25rem' }}>
        {data.t.read('loading_in_progress')}
      </div>
      <div>
        <span className="spinner-border spinner-border-m" role="status" />
      </div>
    </div>
  ) : codeUnitList.length === 0 ? (
    <div className="font-italic">
      {data.t.read('no_existing_code_or_unit_for_client')}
    </div>
  ) : null;

  const selected = content === null && codeUnitList?.length > 0 ? codeUnitList[codeUnitIdx] : null;
  const showTable = !loadError && content === null && selected != null;

  return (
    <div>
      {renderMessageStrip()}
      {content}
      {showTable && (
        <>
          {/* Description of the currently selected code/unit */}
          <div className="font-xsmall mb-2">
        <span>
          {type === 'codes' ? 'Code ' : 'Unité '}
          {type === 'codes' ? selected.code : selected.unit}
          {' : '}
        </span>
        {selected.desc}
      </div>

      <table className="table table-striped table-sm font-small">
        <thead>
          <tr>
            <th>
              {type === 'codes'
                ? 'Code'
                : data.t.read('client_table.units_offices')}
            </th>
            <th>{data.t.read('supplier_name')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {/* Row to add a new association: choose code/unit, pick supplier, click Add */}
          <tr>
            <td>
              <select
                className="form-control form-control-sm"
                style={{ maxWidth: '100px' }}
                value={codeUnitIdx}
                onChange={(e) => setCodeUnitIdx(Number(e.target.value))}
              >
                {codeUnitList.map((elem, idx) => (
                  <option key={idx} value={idx}>
                    {type === 'codes' ? elem.code : elem.unit}
                  </option>
                ))}
              </select>
            </td>
            <td>
              <FormTextField
                format="data"
                value={supplierName}
                hideNA={true}
                hideActionButtons={true}
                hideAddOperation={true}
                searchURL="/suppliers/search/"
                searchCallback={(sup) => {
                  setSupplierId(sup._id);
                  setSupplierName(sup.name);
                }}
              />
            </td>
            <td className="text-center">
              <button
                className="btn btn-info btn-sm"
                onClick={addCode}
                disabled={actionInProgress != null}
              >
                {data.t.read('add')}
              </button>
            </td>
          </tr>

          {/* Until list is loaded, show spinner row; then show one row per association with Delete */}
          {!listLoaded ? (
            <tr>
              <td colSpan="3" className="text-center py-4">
                <div className="d-flex flex-column align-items-center text-dark">
                  <div className="mt-3 mb-2 font-weight-bold" style={{ fontSize: '1.25rem' }}>
                    {data.t.read('loading_in_progress')}
                  </div>
                  <div>
                    <span className="spinner-border spinner-border-m" role="status" />
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            list.map((element) => (
              <tr key={element._id}>
                <td>{element.code ?? element.unit}</td>
                <td>{element.supplier_name}</td>
                <td className="text-center">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteElement(element._id)}
                    disabled={actionInProgress != null}
                  >
                    {data.t.read('delete')}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
        </>
      )}
    </div>
  );
}

export default inject('data')(observer(SupplierUnitsCodes));
